import {
    PipelineDefinition,
    PipelineStepInstance,
} from './pipelineBuilder.types'
import { createStepInstanceForDialog } from './pipelineBuilderStateUtils'
import { cloneDefinition } from './emptyDefinition'
import { migrateDefinitionTransitionConfigs } from './transitionConfigUtils'
import { renumberSteps } from './validation'

export const DEFAULT_PIPELINE_DEFINITION: PipelineDefinition = {
    version: 1,
    steps: [],
}

function createRequiredStep(type: 'prospects' | 'leads'): PipelineStepInstance {
    return createStepInstanceForDialog(type, [])
}

export function ensureRequiredSteps(
    definition: PipelineDefinition,
): PipelineDefinition {
    const next = cloneDefinition(definition)
    let steps = [...(Array.isArray(next.steps) ? next.steps : [])]

    let prospects = steps.find((s) => s.type === 'prospects')
    if (!prospects) {
        prospects = createRequiredStep('prospects')
        steps = [prospects, ...steps]
    }

    let leads = steps.find((s) => s.type === 'leads')
    if (!leads) {
        leads = createRequiredStep('leads')
        const prospectsIndex = steps.findIndex((s) => s.type === 'prospects')
        steps.splice(prospectsIndex + 1, 0, leads)
    }

    steps = steps.map((step) => {
        if (step.type === 'prospects' || step.type === 'leads') {
            return { ...step, enabled: true }
        }
        return step
    })

    const enabled = steps
        .filter((s) => s.enabled)
        .sort((a, b) => a.order - b.order)

    const prospectsEnabledIdx = enabled.findIndex((s) => s.type === 'prospects')
    const leadsEnabledIdx = enabled.findIndex((s) => s.type === 'leads')

    if (prospectsEnabledIdx > 0) {
        const [prospectsStep] = enabled.splice(prospectsEnabledIdx, 1)
        enabled.unshift(prospectsStep)
    }

    const leadsIdxAfterProspects = enabled.findIndex((s) => s.type === 'leads')
    if (leadsIdxAfterProspects > 1) {
        const [leadsStep] = enabled.splice(leadsIdxAfterProspects, 1)
        enabled.splice(1, 0, leadsStep)
    } else if (leadsIdxAfterProspects < 0) {
        const leadsStep = steps.find((s) => s.type === 'leads')
        if (leadsStep) {
            enabled.splice(1, 0, leadsStep)
        }
    }

    const enabledIds = new Set(enabled.map((s) => s.id))
    const benchOrder = new Map(enabled.map((s, i) => [s.id, i + 1]))
    const disabled = steps
        .filter((s) => !enabledIds.has(s.id))
        .sort((a, b) => a.order - b.order)

    const merged = [
        ...enabled.map((s) => ({
            ...s,
            enabled: true,
            order: benchOrder.get(s.id) ?? s.order,
        })),
        ...disabled.map((s, i) => ({
            ...s,
            order: enabled.length + i + 1,
        })),
    ]

    return migrateDefinitionTransitionConfigs({
        ...next,
        steps: renumberSteps(merged),
    })
}

export function definitionFromApi(
    definition: PipelineDefinition | null | undefined,
): PipelineDefinition {
    if (!definition?.steps?.length) {
        return ensureRequiredSteps(cloneDefinition(DEFAULT_PIPELINE_DEFINITION))
    }
    return ensureRequiredSteps(cloneDefinition(definition))
}
